import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { AppContext } from '../contexts/AppContext';
import { getImageUrl, showImageWhenLoaded } from '../common/Media';
import { getTranslation } from '../common/Translation';
import { getReadablePalette, ReadablePalette } from '../common/Color';
import { u } from '../common/Units';
import { DM_DISPLAY_URL, DM_DMS_VIDEO_URL, MEDIA_PREFIX } from '../Settings';
import { DMItem, cleanDMImageUrl, fetchDMArtifact, formatDMYears, getDMImageUrl, getDMTypeKey, getDMVirtualExperienceUrl, getSketchfabEmbedUrl, isDMVirtualExperience } from '../services/DigitaltMuseum';
import Loader from './Loader';
import DMVirtualExperience from './DMVirtualExperience';
import { CubeIcon, PlayIcon, ZoomIcon } from './DMIcons';
import { BlockAudioItem } from './BlockAudio';
import VideoPopup from './LightboxModal_Video';
import Model3dPopup from './LightboxModal_3D';
import VMIcon from '../assets/icon_vm.svg?react';
import CloseIcon from '../assets/icon_close.svg?react';
import { ModalOverlay as Overlay, ModalCloseButton as CloseButton, modalScrollbar as scrollbar, modalSlideIn as slideIn, modalFadeIn as fadeIn, useEscapeKey, useModalOpenClass } from './Modal';

/**
 * Data already known from the search result, shown while the full object is loading. `uuid` and `ownerCode`
 * are needed for virtual experiences, which are not in simple_json
 */
export interface DMObjectPreview extends DMItem {
  /** Owner name from DM's museum register */
  owner?: string
}

/** An image, video, 3D model or virtual experience in the media column */
interface DMMediaItem {
  type: "image" | "video" | "model3d" | "sketchfab" | "vm",
  /** Image URL, or poster for the other types. Can be empty */
  url: string,
  /** DMS media ID for videos */
  dmsId?: string,
  /** File URL for 3D models (glb), embed URL for Sketchfab, or page URL for virtual experiences */
  src?: string,
  /** DMS media ID of an image */
  mediaId?: string,
  photographer?: string
}

interface DMObjectModalProps {
  /** DM unique ID (dimu code) of the object to show */
  uniqueId: string,
  /** Background color of the view. Used to pick surface and text colors */
  backgroundColor?: string,
  /** Data from the search result, shown while loading */
  preview?: DMObjectPreview,
  /** Close the modal. Not used in the page variant */
  onClose?: () => void,
  /** "modal" (default) shows a dialog on top of the view. "page" shows the object as the content of a view */
  variant?: "modal" | "page",
  /** DMS media ID of the image to show first, e.g. the image that was tapped. Defaults to the first */
  initialMediaId?: string
}

const Dialog = styled.div<{ $palette: ReadablePalette }>`
  position: relative;
  display: flex;
  gap: ${u(40)};
  width: calc(100% - ${u(130)});
  max-width: ${u(1500)};
  height: calc(100% - ${u(130)});
  max-height: ${u(880)};
  padding: ${u(40)};
  box-sizing: border-box;
  border-radius: ${u(24)};
  background-color: ${p => p.$palette.surfaceColor};
  color: ${p => p.$palette.textColor};
  box-shadow: 0 ${u(20)} ${u(60)} rgba(0, 0, 0, 0.4);
  animation: ${slideIn} 0.25s ease-out;

  @media (orientation: portrait) {
    flex-direction: column;
    max-height: none;
    overflow-y: auto;
    ${scrollbar}
  }

  &.page {
    width: 100%;
    max-width: none;
    height: 100%;
    max-height: none;
    box-shadow: none;
    animation: none;
  }
`;

const MediaColumn = styled.div`
  flex: 0 0 58%;
  display: flex;
  flex-direction: column;
  gap: ${u(16)};
  min-width: 0;

  @media (orientation: portrait) {
    flex: 0 0 auto;
    height: 55%;
  }
`;

const MainImage = styled.div<{ $palette: ReadablePalette }>`
  position: relative;
  flex: 1;
  min-height: 0;
  border-radius: ${u(16)};
  overflow: hidden;
  background-color: ${p => p.$palette.imageBackgroundColor};
  cursor: zoom-in;

  &.video {
    cursor: pointer;
  }

  & img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: 0;
    transition: opacity 0.3s;
  }
`;

const ZoomButton = styled.button`
  position: absolute;
  bottom: ${u(16)};
  right: ${u(16)};
  width: ${u(64)};
  height: ${u(64)};
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(30, 30, 30, 0.75);
  color: #fff;
  cursor: pointer;

  & svg {
    width: ${u(30)};
    height: ${u(30)};
  }
`;

const PlayOverlay = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${u(110)};
  height: ${u(110)};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(30, 30, 30, 0.75);
  color: #fff;
  pointer-events: none;

  & svg {
    width: ${u(48)};
    height: ${u(48)};
    margin-left: ${u(6)};
  }

  &.model3d {
    flex-direction: column;
    gap: ${u(4)};
    font-weight: 600;
    font-size: 0.9em;

    & svg {
      margin-left: 0;
    }
  }

  &.small {
    width: ${u(40)};
    height: ${u(40)};

    & svg {
      width: ${u(18)};
      height: ${u(18)};
      margin-left: ${u(2)};
    }

    &.model3d svg {
      margin-left: 0;
    }
  }
`;

const AudioList = styled.ul<{ $palette: ReadablePalette }>`
  list-style: none;
  margin: ${u(24)} 0 0;
  padding: 0;

  /* Adapt the shared audio player to the modal colors */
  & li {
    padding: ${u(18)} ${u(22)};
    background-color: ${p => p.$palette.cardColor};
    box-sizing: border-box;
  }

  & li > div > div {
    background-color: ${p => p.$palette.borderColor};
  }

  & li span {
    font-size: 0.9em;
  }
`;

const Thumbnails = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: ${u(12)};
  overflow-x: auto;
  flex: 0 0 auto;
`;

const Thumbnail = styled.li<{ $palette: ReadablePalette }>`
  position: relative;
  flex: 0 0 ${u(130)};
  height: ${u(100)};
  border-radius: ${u(10)};
  overflow: hidden;
  background-color: ${p => p.$palette.imageBackgroundColor};
  border: ${u(3)} solid transparent;
  box-sizing: border-box;
  cursor: pointer;

  &.active {
    border-color: ${p => p.$palette.textColor};
  }

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const InfoColumn = styled.div<{ $palette: ReadablePalette }>`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  /* Just enough room for the scrollbar beside the text */
  padding-right: ${u(20)};
  ${scrollbar}

  @media (orientation: portrait) {
    overflow-y: visible;
    padding-right: 0;
  }
`;

/** Room for the close button beside the type and title, which are at the top of the text column */
const CLOSE_BUTTON_SPACE = u(50);

const Eyebrow = styled.p<{ $palette: ReadablePalette }>`
  margin: 0 ${CLOSE_BUTTON_SPACE} ${u(8)} 0;
  font-size: 0.75em;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${p => p.$palette.mutedTextColor};
`;

const Title = styled.h2`
  margin: 0 ${CLOSE_BUTTON_SPACE} ${u(12)} 0;
  font-size: 1.7em;
  line-height: 1.15;
`;

const Subtitle = styled.p`
  margin: 0 0 ${u(6)};
`;

const Description = styled.p`
  margin: ${u(24)} 0;
  white-space: pre-line;
`;

const Separator = styled.hr<{ $palette: ReadablePalette }>`
  border: none;
  border-top: 1px solid ${p => p.$palette.borderColor};
  margin: ${u(24)} 0;
`;

const Fields = styled.dl<{ $palette: ReadablePalette }>`
  display: grid;
  grid-template-columns: minmax(${u(140)}, max-content) 1fr;
  gap: ${u(10)} ${u(24)};
  margin: 0;
  font-size: 0.9em;

  & dt {
    color: ${p => p.$palette.mutedTextColor};
  }

  & dd {
    margin: 0;
  }
`;

const QRWrapper = styled.div<{ $palette: ReadablePalette }>`
  display: none;
  align-items: center;
  gap: ${u(20)};
  margin-top: ${u(28)};
  font-size: 0.9em;
  color: ${p => p.$palette.mutedTextColor};

  & > div {
    flex: 0 0 auto;
    padding: ${u(10)};
    background: #fff;
    border-radius: ${u(8)};
    line-height: 0;
  }

  & svg {
    width: ${u(80)};
    height: ${u(80)};
  }
`;

const ZoomLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 45;
  background-color: #000;
  animation: ${fadeIn} 0.2s;

  & .react-transform-wrapper,
  & .react-transform-component {
    width: 100% !important;
    height: 100% !important;
  }

  & img {
    width: 100vw;
    height: 100vh;
    object-fit: contain;
  }
`;

/**
 * Sketchfab always shows the title, owner and logo (with links to sketchfab.com) for models owned by
 * free accounts, regardless of the ui_* embed options. The iframe is made taller than its wrapper so these
 * bars are cropped away. The sandbox (no allow-popups / allow-top-navigation) blocks any remaining links.
 */
const SKETCHFAB_CROP_TOP = 80;
const SKETCHFAB_CROP_BOTTOM = 52;

const SketchfabWrapper = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
`;

const SketchfabFrame = styled.iframe`
  position: absolute;
  top: -${SKETCHFAB_CROP_TOP}px;
  left: 0;
  width: 100%;
  height: calc(100% + ${SKETCHFAB_CROP_TOP + SKETCHFAB_CROP_BOTTOM}px);
  border: none;
`;

const VMOverlay = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: ${u(14)};
  padding: ${u(18)} ${u(30)};
  border-radius: ${u(40)};
  background-color: rgba(30, 30, 30, 0.8);
  color: #fff;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;

  & svg {
    width: ${u(34)};
    height: ${u(38)};
  }
`;

const ErrorText = styled.p<{ $palette: ReadablePalette }>`
  color: ${p => p.$palette.mutedTextColor};
`;

/** Image URL with dimension and media prefix */
const imageSrc = (url: string, dimension: string) => `${MEDIA_PREFIX}${getImageUrl(url, { dimension })}`;

const is3d = (type?: DMMediaItem["type"]) => type === "model3d" || type === "sketchfab";

/** Translation key describing a media item (used for labels) */
const MEDIA_LABEL_KEYS: Record<DMMediaItem["type"], string> = {
  image: "dm.image",
  video: "dm.video",
  model3d: "dm.model3d",
  sketchfab: "dm.model3d",
  vm: "dm.types.VirtualExperience"
};

/** Translation key for the action when the main media is tapped */
const MEDIA_ACTION_KEYS: Record<DMMediaItem["type"], string> = {
  image: "dm.zoom",
  video: "dm.playVideo",
  model3d: "dm.open3d",
  sketchfab: "dm.open3d",
  vm: "dm.openVM"
};

/**
 * Get a displayable text from a DM field. DM sometimes returns empty objects or other
 * non-text values (e.g. `"name": {}`), which would otherwise render as "[object Object]".
 * Language maps are resolved with getTranslation.
 * @param {any} value Field value
 * @param {string} language Current language
 * @returns {string} Trimmed text, or an empty string
 */
const toText = (value: any, language?: string): string => {
  if (typeof value === "string") { return value.trim(); }
  if (typeof value === "number") { return String(value); }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const translated = getTranslation(value, language);
    return typeof translated === "string" ? translated.trim() : "";
  }
  return "";
}

/**
 * Join a list of DM values to a comma separated text, skipping empty and non-text values
 * @param {any[]} values Values
 * @param {string} language Current language
 * @returns {string} Comma separated text
 */
const joinText = (values: any[] | undefined, language?: string): string => {
  return (values || []).map(v => toText(v, language)).filter(Boolean).join(", ");
}

/**
 * Check if a DM file is of the given type. Older objects have no file type, then the object type is used
 * @param {object} file DM file
 * @param {string} type File type
 * @param {string} artifactType DM object type
 * @returns {boolean}
 */
const isFileOfType = (file: any, type: "audio" | "video" | "model_3d", artifactType?: string) => {
  return (file?.fileType || "").toLowerCase() === type || (!file?.fileType && (artifactType || "").toLowerCase() === type);
}

/**
 * Get the media to show for an object: virtual experience first, then images, 3D models and videos
 * (videos first for video objects). Falls back to the image from the search result while loading.
 * @param {object} artifact Object from DM (simple_json). Null while loading
 * @param {DMObjectPreview} preview Data from the search result
 * @param {string} language Current language
 * @returns {DMMediaItem[]} Media items
 */
const getMediaItems = (artifact: any, preview: DMObjectPreview | undefined, language?: string): DMMediaItem[] => {
  if (!artifact) {
    return preview?.mediaIdentifier ? [{ type: "image", url: getDMImageUrl(preview.mediaIdentifier) }] : [];
  }

  const files: any[] = artifact.files || [];
  const images: DMMediaItem[] = (artifact.images || []).map((image: any) => ({
    type: "image",
    url: cleanDMImageUrl(image?.url) || getDMImageUrl(image?.mediaId),
    mediaId: image?.mediaId,
    photographer: toText(image?.photographer, language)
  }));
  // Other media use the first image as poster
  const poster = images[0]?.url || "";

  const videos: DMMediaItem[] = files
    .filter(file => file?.mediaId && isFileOfType(file, "video", artifact.type))
    .map(file => ({ type: "video", url: getDMImageUrl(file.mediaId), dmsId: file.mediaId }));

  const models: DMMediaItem[] = files
    .filter(file => (file?.url || file?.mediaId) && isFileOfType(file, "model_3d"))
    .map(file => ({ type: "model3d", url: poster, src: file.url || `https://ems.dimu.org/file/${file.mediaId}` }));

  // Sketchfab models linked in references
  const sketchfabUrls = (artifact.references || []).map((reference: any) => getSketchfabEmbedUrl(reference?.url)).filter(Boolean) as string[];
  [...new Set(sketchfabUrls)].forEach(src => models.push({ type: "sketchfab", url: poster, src }));

  const vmUrl = isDMVirtualExperience(artifact.subtype || preview?.subtype) && getDMVirtualExperienceUrl(preview?.uuid, language, preview?.ownerCode);
  const experiences: DMMediaItem[] = vmUrl ? [{ type: "vm", url: poster, src: vmUrl }] : [];

  return artifact.type === "Video" ? [...experiences, ...videos, ...images, ...models] : [...experiences, ...images, ...models, ...videos];
}

/**
 * A single DigitaltMuseum object with media and metadata, as a modal or as the content of a view (variant "page").
 * Reusable for any DM object identified by a DM unique ID. Not intended for DM images added through the media block.
 * @returns {JSX.Element} Component template
 */
const DMObjectModal: FC<DMObjectModalProps> = ({ uniqueId, backgroundColor, preview, onClose = () => null, variant = "modal", initialMediaId }) => {
  const isPage = variant === "page";
  const { t } = useTranslation();
  const { theme, setMediaIsPlaying, currentLanguage } = useContext(AppContext);
  const [artifact, setArtifact] = useState<any>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [fullscreenIsOpen, setFullscreenIsOpen] = useState<boolean>(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const palette = useMemo(() => getReadablePalette(backgroundColor, theme?.backgroundColor), [backgroundColor, theme?.backgroundColor]);

  useModalOpenClass(!isPage);

  // Fetch object metadata
  useEffect(() => {
    let isSubscribed = true;
    setArtifact(null);
    setHasError(false);
    setActiveIndex(0);

    fetchDMArtifact(uniqueId).then(data => {
      if (!isSubscribed) { return; }
      data?.unique_id ? setArtifact(data) : setHasError(true);
    }).catch(() => {
      isSubscribed && setHasError(true);
    });

    return () => { isSubscribed = false };
  }, [uniqueId]);

  /**
   * Close the fullscreen image or video
   */
  const closeFullscreen = useCallback(() => {
    setFullscreenIsOpen(false);
    setMediaIsPlaying && setMediaIsPlaying(false);
  }, [setMediaIsPlaying]);

  // Media is no longer playing when the modal is closed
  useEffect(() => {
    return () => { setMediaIsPlaying && setMediaIsPlaying(false); };
  }, [setMediaIsPlaying]);

  // Focus the dialog for screen readers
  useEffect(() => { !isPage && dialogRef.current?.focus(); }, [isPage]);

  // Escape closes the fullscreen view first, then the dialog
  const onEscape = useCallback(() => { fullscreenIsOpen ? closeFullscreen() : !isPage && onClose(); }, [fullscreenIsOpen, closeFullscreen, isPage, onClose]);
  useEscapeKey(onEscape);

  const mediaItems = useMemo(() => getMediaItems(artifact, preview, currentLanguage), [artifact, preview, currentLanguage]);

  // Audio files, played with the shared audio player
  const audioFiles: string[] = useMemo(() => {
    return (artifact?.files || [])
      .filter((file: any) => file?.mediaId && isFileOfType(file, "audio", artifact?.type))
      .map((file: any) => file.mediaId);
  }, [artifact]);

  // Start on the given image once the object has loaded (not again on a change of language)
  useEffect(() => {
    if (!artifact || !initialMediaId) { return; }
    const index = mediaItems.findIndex(item => item.mediaId === initialMediaId);
    index > 0 && setActiveIndex(index);
  }, [artifact, initialMediaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeItem = mediaItems[activeIndex];

  // Production year from the production event, or from the search result
  const year = useMemo(() => {
    const production = artifact?.history?.events?.find((e: any) => e?.timespan && (e?.eventType || "").toLowerCase().startsWith("produksjon"));
    return formatDMYears(production?.timespan?.fromYear, production?.timespan?.toYear) || preview?.year || "";
  }, [artifact, preview]);

  const lang = currentLanguage;
  const typeKey = getDMTypeKey((artifact || preview)?.type, (artifact || preview)?.subtype);
  const typeLabel = t(`dm.type.${typeKey}`, { defaultValue: typeKey });
  const identifier = toText(artifact?.identifier, lang);
  // The name from DM's museum register (via the search result) is preferred: the object data sometimes has a collection name
  const owner = preview?.owner || toText(artifact?.partOfCollection?.owner?.name, lang);
  const alternativeNames = joinText(artifact?.names?.filter((n: any) => n?.type !== "name")?.map((n: any) => n?.name), lang);
  const title = toText(artifact?.title, lang) || preview?.title || "";
  const producer = toText(artifact?.producer, lang);
  const description = toText(artifact?.description, lang);

  // Metadata fields. Empty values are not shown
  const history = toText(artifact?.history?.description, lang);
  const motif = toText(artifact?.motif?.description, lang);

  const fields = [
    { label: t("dm.field.names"), value: alternativeNames },
    { label: t("dm.field.producer"), value: producer },
    { label: t("dm.field.placeOfProduction"), value: toText(artifact?.placeOfProduction, lang) },
    { label: t("dm.field.dating"), value: year },
    { label: t("dm.field.classification"), value: joinText(artifact?.classifications?.map((c: any) => c?.description), lang) },
    { label: t("dm.field.owner"), value: owner },
    // Articles, virtual experiences etc. use the DM ID as identifier, which is not an inventory number
    { label: t("dm.field.identifier"), value: identifier !== toText(artifact?.unique_id, lang) ? identifier : "" },
    { label: t("dm.field.subjects"), value: joinText(artifact?.subjects?.map((s: any) => s?.name), lang) },
    // Photographer of the image shown
    { label: t("dm.field.photographer"), value: activeItem?.photographer }
  ].filter(field => field.value);

  // Hide the media column if the object only has audio or text
  const showMediaColumn = mediaItems.length > 0 || !artifact;

  const dialog = (
      <Dialog ref={dialogRef} $palette={palette} className={isPage ? "page scroll" : "scroll"} role={isPage ? undefined : "dialog"} aria-modal={isPage ? undefined : true} aria-labelledby="dmObjectTitle" tabIndex={-1} onClick={e => e.stopPropagation()}>
        {!isPage && <CloseButton $palette={palette} onClick={onClose} aria-label={t("dm.close")}><CloseIcon/></CloseButton>}

        {showMediaColumn && (
          <MediaColumn>
            <MainImage $palette={palette} className={activeItem && activeItem.type !== "image" ? "video" : undefined} onClick={() => activeItem && setFullscreenIsOpen(true)}
              role={activeItem ? "button" : undefined} aria-label={activeItem ? t(MEDIA_ACTION_KEYS[activeItem.type]) : undefined}>
              {activeItem && (
                <>
                  {activeItem.url && <img key={activeItem.url + activeItem.type} src={imageSrc(activeItem.url, "1200x1200")} alt={title} onLoad={showImageWhenLoaded}/>}
                  {activeItem.type === "video" && <PlayOverlay><PlayIcon/></PlayOverlay>}
                  {is3d(activeItem.type) && <PlayOverlay className="model3d"><CubeIcon/><span>3D</span></PlayOverlay>}
                  {activeItem.type === "vm" && <VMOverlay><VMIcon/><span>{t("dm.openVM")}</span></VMOverlay>}
                  {activeItem.type === "image" && <ZoomButton tabIndex={-1} aria-hidden="true"><ZoomIcon/></ZoomButton>}
                </>
              )}
            </MainImage>
            {mediaItems.length > 1 && (
              <Thumbnails>
                {mediaItems.map((item, i) => (
                  <Thumbnail key={`${item.type}${item.src || item.dmsId || item.url}`} $palette={palette} className={i === activeIndex ? "active" : undefined} onClick={() => setActiveIndex(i)}
                    aria-label={`${t(MEDIA_LABEL_KEYS[item.type])} ${i + 1}`}>
                    {item.url && <img src={imageSrc(item.url, "250x250")} alt=""/>}
                    {item.type === "video" && <PlayOverlay className="small"><PlayIcon/></PlayOverlay>}
                    {item.type === "vm" && <PlayOverlay className="small model3d"><VMIcon/></PlayOverlay>}
                    {is3d(item.type) && <PlayOverlay className="small model3d"><CubeIcon/></PlayOverlay>}
                  </Thumbnail>
                ))}
              </Thumbnails>
            )}
          </MediaColumn>
        )}

        <InfoColumn className="scroll column" $palette={palette}>
          {typeLabel && <Eyebrow $palette={palette}>{typeLabel}</Eyebrow>}
          <Title id="dmObjectTitle">{title}</Title>
          {producer && <Subtitle>{producer}</Subtitle>}
          {(year || owner) && <Subtitle>{[year, owner].filter(Boolean).join(" • ")}</Subtitle>}

          {!artifact && !hasError && <Loader variant="inline" size={44} color={palette.mutedTextColor}/>}
          {hasError && <ErrorText $palette={palette}>{t("dm.objectError")}</ErrorText>}

          {audioFiles.length > 0 && (
            <AudioList $palette={palette}>
              {audioFiles.map((dmsId, i) => (
                <BlockAudioItem key={dmsId} media={{ dmsId, media_type: "audio", title: audioFiles.length > 1 ? `${t("dm.audio")} ${i + 1}` : undefined }}/>
              ))}
            </AudioList>
          )}

          {[description, history, motif].filter(Boolean).map(text => <Description key={text}>{text}</Description>)}

          {fields.length > 0 && (
            <>
              <Separator $palette={palette}/>
              <Fields $palette={palette}>
                {fields.map(field => (
                  <div key={field.label} style={{ display: "contents" }}>
                    <dt>{field.label}</dt>
                    <dd>{field.value}</dd>
                  </div>
                ))}
              </Fields>
            </>
          )}

          {artifact && (
            <QRWrapper $palette={palette}>
              <div><QRCode value={DM_DISPLAY_URL.replace("{id}", uniqueId)} size={96}/></div>
              <span>{t("dm.scanToReadMore")}</span>
            </QRWrapper>
          )}
        </InfoColumn>
      </Dialog>
  );

  const fullscreen = fullscreenIsOpen && activeItem && (
        <ZoomLayer onClick={e => e.stopPropagation()}>
          {activeItem.type === "vm" && preview?.uuid ? (
            <DMVirtualExperience uuid={preview.uuid} ownerCode={preview.ownerCode} title={title} palette={getReadablePalette("#000")}/>
          ) : activeItem.type === "sketchfab" && activeItem.src ? (
            <SketchfabWrapper>
              <SketchfabFrame src={activeItem.src} title={title} sandbox="allow-scripts allow-same-origin" allow="autoplay; xr-spatial-tracking"/>
            </SketchfabWrapper>
          ) : activeItem.type === "model3d" && activeItem.src ? (
            <Model3dPopup item={{ src: activeItem.src }} close={closeFullscreen}/>
          ) : activeItem.type === "video" && activeItem.dmsId ? (
            <VideoPopup close={closeFullscreen} item={{
              url: DM_DMS_VIDEO_URL.replaceAll("{id}", activeItem.dmsId),
              thumbnail_src: getImageUrl(activeItem.url, { dimension: "1200x1200" })
            }}/>
          ) : (
            <TransformWrapper minScale={1} maxScale={6} centerOnInit={true} doubleClick={{ mode: "toggle" }}>
              <TransformComponent>
                <img src={imageSrc(activeItem.url, "3000")} alt={title}/>
              </TransformComponent>
            </TransformWrapper>
          )}
          <CloseButton $palette={getReadablePalette("#000")} onClick={closeFullscreen} aria-label={t("dm.close")}><CloseIcon/></CloseButton>
        </ZoomLayer>
  );

  if (isPage) {
    return <>{dialog}{fullscreen}</>;
  }

  return (
    <Overlay onClick={onClose}>
      {dialog}
      {fullscreen}
    </Overlay>
  );
}

export default DMObjectModal;
